/**
 * Sequence (program) preview. A small compositor over proxies: one media
 * element per clip in view, stacked like the export (V1 with its transitions,
 * then video layers with opacity, size and position; alpha-channel media keep
 * their transparency through transparent proxies), A1/A2 and layer sound at
 * their gains, graphics (same sandboxed host as export) and subtitles on top.
 * Playback follows the wall clock and nudges each element back into sync.
 * Browser preview timing is approximate; the export renders authoritative timing.
 *
 * 0.96.0: sound goes through Web Audio (previewAudio.ts: clip gain above 0 dB, sound fades, track mute/solo, the
 * meter); picture fades (V1 to black via brightness, layers to transparent via opacity); a mini timeline, a
 * transport row and a button row lined up with the source viewer's; J/K/L shuttle; typed timecode; and the live
 * playhead goes to the timeline through the playhead clock instead of re-rendering the editor on every frame.
 */
import { PaneControls } from '../../components/Panes';
import { useElementSize, useFittedBox, useVideoContentRect } from '../../player/videoContentRect';
import { CueOverlay } from '../../subtitles/CueOverlay';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent as ReactMouseEvent, type ReactNode, type PointerEvent as ReactPointerEvent } from 'react';
import { BookmarkPlus, ChevronLeft, ChevronRight, Layers, Maximize2, Minimize2, Pause, Play, SquareDashed, Volume1, Volume2, VolumeX } from 'lucide-react';
import {
  BLUR_FIT_SIGMA, PRIMARY_FORMAT, TICKS_PER_SECOND, allClips, captionLayout, ops, clipDuration, cropAt, cssFit, documentForFormat, fadeFactor, framingAt, graphicPlacement, framesToTimecode, formatDuration, formatTimecode, graphicLayers, layersAt, layoutVideo, pictureFades, renderAtData, resolveAt, safeZonesFor, sequenceDuration,
  contrastCurve, soundFadeFactor, storylinePictureFades, svgColorMatrixValues, ticksPerFrame, ticksToFrames, trackAudible, allAudioTracks, graphicOrder, graphicTrackOf,
  type FormatRef, type FramingLike, type GraphicTemplateDTO, type OgrafManifest, type PlatformSafeZone, type SequenceDocument, type Size,
} from '@prism/contracts';
import { formatColor, useFormatView, type EditorApply } from './formatContext';
import { FormatBar } from './FormatBar';
import { FormatGuides } from './FormatGuides';
import { SideBySide } from './SideBySide';
import { coverSource, cropClipPath, cropTransform, panByPictureDrag, panKeepingCentre, sideBySideSizes, windowSize, zoomByPinch, zoomByWheel } from './monitor-geometry';
import { editTargetAt, framingEdit } from './monitor-edit';
import { layerPlaceAt, mediaSize, type PictureElement } from './monitor-paint';
import { NO_CROSSFADES, crossfadeFactor, crossfadePlan, levelGain, prerollingAt, sourceLengths, type CrossfadePlan } from './audioPreview';
import { subtitleCoalesce, subtitlePct } from './SubtitleHeight';
import { Timeline as BarTimeline } from '../../player/PlayerTimeline';
import { usePref } from '../../lib/prefs';
import { useClockValue, type PlayheadClock } from './playheadClock';
import type { PreviewAudio } from './previewAudio';
import { TimecodeField } from './TimecodeField';
import { nextShuttle } from './timelineTools';
import { OgrafFrame, type OgrafFrameHandle, type OgrafMount } from '../../graphics/OgrafFrame';
import { clipMoment, clipSchedule, isOgrafTemplate, templateFor } from '../../graphics/ograf';
import { useOgrafPackages } from '../../graphics/ografPackage';
import { useTemplateBundle, type SequenceSource } from '../../api/hooks';
import { GraphicPreview } from '../../components/GraphicPreview';
import { isTyping } from '../../lib/keys';
import { attachHls, type HlsAttachment } from '../../player/hls';
import { FIRST_FRAME_TIMEOUT_MS, PREROLL_VOLUME, clockBaseFromFrame, continuityHeads, currentLead, followClock, noteStartLatency, planUpcoming, setStyles, setVolume, startHeld, type TrackClip } from './mediaSync';

const S = TICKS_PER_SECOND;
/** Elements for clips starting this soon are created and cued in advance (longer than the longest pre-roll). */
const LOOKAHEAD = 2.5 * S;
/** Inline style of a shown element before the clip's own values are applied. */
const STYLE_RESET = { visibility: 'visible', opacity: '1', transform: '', transformOrigin: '', clipPath: '', zIndex: '', width: '', height: '', left: '', top: '', filter: '', objectFit: '', objectPosition: '' };

/**
 * Graphics stack strictly in track order (0.93.1), the order the export composes them in: every graphic gets its
 * index in `doc.graphics` as its z-index. renderAt templates each run in a frame of their own; OGraf graphics share
 * one host frame per contiguous run of OGraf graphics in that order (graphicLayers), placed at the run's index, so
 * a renderAt graphic between two OGraf graphics is drawn between them, as in the export. 1.7.3: "track order" is
 * graphicOrder (GFX 1 to GFX 3, then array order); a track hidden in the preview is left out here only.
 */
function GraphicLayers({ doc: full, playhead, templates, clock, hidden = [] }: { doc: SequenceDocument; playhead: number; templates: GraphicTemplateDTO[]; clock: Set<(ph: number) => void>; hidden?: number[] }) {
  // 1.7.3: in drawing order (graphicOrder: GFX 1 at the bottom to GFX 3 on top, then array order), without the
  // graphics tracks hidden in the preview (a view-only toggle; the export draws every track).
  const hiddenKey = hidden.join(',');
  const doc = useMemo(() => {
    const order = graphicOrder(full).filter((g) => !hidden.includes(graphicTrackOf(g)));
    return order.length === full.graphics.length && order.every((g, i) => g === full.graphics[i]) ? full : { ...full, graphics: order };
  }, [full, hiddenKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const ograf = (g: SequenceDocument['graphics'][number]) => isOgrafTemplate(templateFor(templates, g));
  const layers = graphicLayers(doc.graphics, ograf);
  return (
    <>
      {layers.map((l) => {
        if (l.kind === 'ograf') return <OgrafRun key={`ograf-${doc.graphics[l.indexes[0]!]!.id}`} doc={doc} playhead={playhead} templates={templates} indexes={l.indexes} clock={clock} />;
        const g = doc.graphics[l.index]!;
        if (playhead < g.recordInTicks || playhead >= g.recordInTicks + g.durationTicks) return null;
        return <GraphicInstance key={g.id} g={g} z={l.index} t={(playhead - g.recordInTicks) / S} template={templateFor(templates, g)} frame={doc} />;
      })}
    </>
  );
}

function GraphicInstance({ g, t, z, template, frame }: { g: SequenceDocument['graphics'][number]; t: number; z: number; template: GraphicTemplateDTO | undefined; frame: { width: number; height: number } }) {
  const bundle = useTemplateBundle(template?.id, template?.version);
  if (!template || !bundle.data) return null;
  // A template authored at another shape than the sequence (1.0.0-rc.11) is fitted and centred, as the export renders it.
  const place = graphicPlacement({ width: template.manifest.width, height: template.manifest.height }, frame);
  const box: CSSProperties = place.fitted
    ? { position: 'absolute', left: `${(place.left / frame.width) * 100}%`, top: `${(place.top / frame.height) * 100}%`, width: `${(place.width / frame.width) * 100}%`, zIndex: z }
    : { position: 'absolute', inset: 0, width: '100%', zIndex: z };
  return <GraphicPreview bundle={bundle.data} manifest={template.manifest} data={renderAtData(g.data)} t={t} durationSeconds={g.durationTicks / S} style={box} />;
}

/**
 * One run of OGraf graphics (0.93.0, one frame per run since 0.93.1): a sandboxed host frame at the sequence's
 * picture size with every graphic of the run on screen or starting within the LOOKAHEAD (pre-mounted hidden, since
 * load() is async), stacked in track order inside it, and the frame itself at the run's place in the stack. Each is
 * seeked to the picture's frame: on every playhead change while paused, and on every animation frame of playback
 * (through `clock`), coalesced by the frame.
 */
function OgrafRun({ doc, playhead, templates, indexes, clock }: { doc: SequenceDocument; playhead: number; templates: GraphicTemplateDTO[]; indexes: number[]; clock: Set<(ph: number) => void> }) {
  const all = indexes.map((z) => ({ g: doc.graphics[z]!, z, t: templateFor(templates, doc.graphics[z]!) })).filter((x): x is { g: typeof x.g; z: number; t: GraphicTemplateDTO & { ograf: OgrafManifest } } => isOgrafTemplate(x.t));
  const near = all.filter(({ g }) => playhead < g.recordInTicks + g.durationTicks && g.recordInTicks <= playhead + LOOKAHEAD);
  const nearKey = near.map((x) => x.g.id).join(',');
  const pkgKeys = useMemo(() => [...new Map(near.map((x) => [`${x.t.id}@${x.t.version}`, { id: x.t.id, version: x.t.version }])).values()], [nearKey, doc]); // eslint-disable-line react-hooks/exhaustive-deps
  const pkgs = useOgrafPackages(pkgKeys);
  const loaded = pkgs.map((q) => q.data?.key ?? '').join(',');
  const mounts = useMemo<OgrafMount[]>(() => near.flatMap(({ g, z, t }) => {
    const pkg = pkgs.find((q) => q.data?.key === `${t.id}@${t.version}`)?.data;
    return pkg ? [{ instance: g.id, pkg, manifest: t.ograf, data: g.data, schedule: clipSchedule(t.ograf, g, doc.frameRate), frameRate: doc.frameRate, z }] : [];
  }), [nearKey, loaded, doc]); // eslint-disable-line react-hooks/exhaustive-deps
  const frame = useRef<OgrafFrameHandle>(null);
  const seekAt = useRef((_ph: number) => {});
  seekAt.current = (ph: number) => frame.current?.seek(near.map(({ g }) => ({ instance: g.id, ...clipMoment(g, doc.frameRate, ph) })));
  useEffect(() => { seekAt.current(playhead); }, [playhead, mounts]);
  useEffect(() => {
    const onFrame = (ph: number) => seekAt.current(ph);
    clock.add(onFrame);
    return () => { clock.delete(onFrame); };
  }, [clock]);
  if (!all.length) return null;
  return <OgrafFrame ref={frame} stage={{ width: doc.width, height: doc.height }} mounts={mounts} title="Graphics preview" style={{ position: 'absolute', inset: 0, width: '100%', zIndex: indexes[0] }} />;
}

/** `live` (1.0.0-rc.1): the url is a recording's snapshot playlist, played through hls.js (Safari: its own player). */
interface Slot { key: string; kind: 'video' | 'image' | 'audio'; url: string; live?: boolean; /** 1.7.0: the source version (its picture size, for crops). */ versionId?: string }

/**
 * A clip from a recording still recording (1.0.0-rc.1): the snapshot playlist of what is recorded so far. When the
 * clip reaches past what the element has loaded (it was trimmed out further) and the element is paused, the
 * snapshot is loaded again. A new React key (`~live`) gives it its own element, so the switch to the final MP4
 * proxy when the recording ends starts from a clean one.
 */
function LiveMedia({ kind, url, needSeconds, mediaRef, onLoadedMetadata, clipKey }: {
  kind: 'video' | 'audio'; url: string; needSeconds: number; clipKey: string;
  mediaRef: (el: HTMLVideoElement | HTMLAudioElement | null) => void; onLoadedMetadata: () => void;
}) {
  const el = useRef<HTMLVideoElement | HTMLAudioElement | null>(null);
  const attachment = useRef<HlsAttachment | null>(null);
  const setRef = useCallback((e: HTMLVideoElement | HTMLAudioElement | null) => { el.current = e; mediaRef(e); }, [mediaRef]);
  const loaded = useRef<string | null>(null);
  useEffect(() => () => { attachment.current?.destroy(); attachment.current = null; loaded.current = null; }, []);
  useEffect(() => {
    const v = el.current;
    if (!v) return;
    // A new link (the signed URL's window moved on) is taken up when the element next pauses, not mid-play.
    const apply = () => {
      if (loaded.current === url) return;
      attachment.current?.destroy();
      attachment.current = attachHls(v, url);
      loaded.current = url;
    };
    if (!attachment.current || v.paused) { apply(); return; }
    v.addEventListener('pause', apply, { once: true });
    return () => v.removeEventListener('pause', apply);
  }, [url]);
  useEffect(() => {
    const v = el.current;
    if (v && v.paused && Number.isFinite(v.duration) && v.duration > 0 && v.duration < needSeconds - 0.1) attachment.current?.refresh();
  }, [needSeconds]);
  return kind === 'audio'
    ? <audio ref={setRef} preload="auto" crossOrigin="anonymous" data-live="true" onLoadedMetadata={onLoadedMetadata} />
    : <video ref={setRef} className="program-layer" playsInline preload="auto" crossOrigin="anonymous" style={{ visibility: 'hidden' }} data-clip={clipKey} data-live="true"
        onLoadedMetadata={onLoadedMetadata} />;
}
/**
 * A keyed or zoomed Fill crop (1.7.0): drawn as a transform of the element (object-fit: fill) instead of object-fit
 * cover, at the element's own time while playing. `pre` goes first in the transform list (a slide's translate);
 * `wipe` is a wipe's progress, its inset computed in the element's own coordinates.
 */
interface CropSpec { framing: FramingLike; frameAspect: number; pre: string; wipe?: number }
interface Active { key: string; seconds: number; volume: number; style: CSSProperties; crop?: CropSpec; /** 1.7.0: a Blur fit (V1): the blurred background follows this element. */ blur?: boolean }

const noApply: EditorApply = () => undefined;
const NO_TRACKS: number[] = [];

/** Whether a clip's crop needs the transform path (keys or a zoom); legacy Fill and Fit keep object-fit, as before 1.7.0. */
const needsCrop = (c: FramingLike) => c.fit === 'fill' && Boolean(c.keys?.length || (c.zoom !== undefined && c.zoom > 1));
interface Upcoming { key: string; /** First source second of the clip. */ seconds: number; /** Seconds until its cut. */ dt: number }

/** Clip id → id of the clip whose element it uses: clips that continue each other exactly share one element. */
function elementHeads(doc: SequenceDocument): Map<string, string> {
  const tol = ticksPerFrame(doc.frameRate);
  const heads = new Map<string, string>();
  const add = (clips: TrackClip[]) => { for (const [k, v] of continuityHeads(clips, tol)) heads.set(k, v); };
  add(layoutVideo(doc).map((p) => ({ id: p.clip.id, versionId: p.clip.versionId, recordIn: p.recordIn, recordOut: p.recordOut, sourceIn: p.clip.sourceInTicks, sourceOut: p.clip.sourceOutTicks, transitionIn: !!p.transition })));
  for (const l of doc.layers ?? []) add(l.clips.slice().sort((a, b) => a.recordInTicks - b.recordInTicks).map((c) => ({ id: c.id, versionId: c.versionId, recordIn: c.recordInTicks, recordOut: c.recordInTicks + clipDuration(c), sourceIn: c.sourceInTicks, sourceOut: c.sourceOutTicks })));
  // 1.7.3: every audio track (A2–A8), each on its own.
  for (const t of allAudioTracks(doc)) add(t.clips.slice().sort((a, b) => a.recordInTicks - b.recordInTicks).map((c) => ({ id: c.id, versionId: c.versionId, recordIn: c.recordInTicks, recordOut: c.recordInTicks + clipDuration(c), sourceIn: c.sourceInTicks, sourceOut: c.sourceOutTicks })));
  return heads;
}

/** The SVG filter id of a clip's colour correction (0.96.0). */
const ccId = (clipId: string) => `cc-${clipId}`;
/**
 * A clip's CSS filter: its colour correction (an SVG filter, color.ts), then its fade to black (brightness), in the
 * export's order. Empty when it has neither.
 */
function pictureFilter(c: { id: string; color?: unknown }, fade: number): string {
  return [c.color ? `url(#${ccId(c.id)})` : '', fade < 1 ? `brightness(${fade.toFixed(4)})` : ''].filter(Boolean).join(' ');
}

/**
 * The colour corrections of the clips as SVG filters, on gamma-encoded values (sRGB interpolation), as FFmpeg does:
 * feColorMatrix with the combined matrix, then a linear transfer for the contrast pivot. Both clamp to 0…1.
 */
function ColorFilters({ doc }: { doc: SequenceDocument }) {
  const clips = [...doc.video, ...(doc.layers ?? []).flatMap((l) => l.clips)].filter((c) => c.color);
  if (!clips.length) return null;
  return (
    <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden data-testid="color-filters">
      <defs>
        {clips.map((c) => {
          const { slope, intercept } = contrastCurve(c.color!);
          return (
            <filter key={c.id} id={ccId(c.id)} colorInterpolationFilters="sRGB" x="0" y="0" width="1" height="1">
              <feColorMatrix type="matrix" values={svgColorMatrixValues(c.color!)} />
              <feComponentTransfer>
                <feFuncR type="linear" slope={slope} intercept={intercept} />
                <feFuncG type="linear" slope={slope} intercept={intercept} />
                <feFuncB type="linear" slope={slope} intercept={intercept} />
              </feComponentTransfer>
            </filter>
          );
        })}
      </defs>
    </svg>
  );
}

/** What is seen and heard at a position: storyline (with transition), layers, A2. */
/**
 * 1.7.4: every sound's level is levelGain (its gain, its volume keys at that source time and its track's gain; above
 * 1 is heard through the element's gain node), times its fades and crossfades (audioPreview.ts). The viewer runs this
 * on every frame it draws and at every seek, so the gain nodes follow the keys and are re-planned when it jumps.
 */
function frameAt(doc: SequenceDocument, heads: Map<string, string>, ph: number, vfades: ReturnType<typeof storylinePictureFades>, xp: CrossfadePlan = NO_CROSSFADES): { active: Active[]; upcoming: Upcoming[] } {
  const active: Active[] = [];
  const k = (id: string) => heads.get(id) ?? id;
  // Track mute and solo (0.96.0), as the export applies them.
  const a1 = trackAudible(doc, 'a1');
  // 1.7.3: a V1 clip whose sound was unlinked plays its picture silent; its sound is a clip on an audio track.
  const heard = (c: { muted: boolean; soundDetached?: true }) => !c.muted && a1 && !c.soundDetached;
  const hit = resolveAt(doc, ph);
  if (hit) {
    const inc = hit.placed.clip;
    const out = hit.outgoing;
    const p = out?.progress ?? 1;
    const kind = hit.placed.transition?.kind;
    // Fit or Fill and the reframe offset (1.0.0-rc.11), the geometry the export uses (contracts framing.ts).
    const inStyle: CSSProperties = { zIndex: 2, ...cssFit(inc.fit, inc.reframeX, inc.reframeY) };
    const outStyle: CSSProperties = { zIndex: 1, ...(out ? cssFit(out.placed.clip.fit, out.placed.clip.reframeX, out.placed.clip.reframeY) : {}) };
    if (out && kind === 'dissolve') inStyle.opacity = p;
    else if (out && kind === 'dip_to_black') { inStyle.opacity = p < 0.5 ? 0 : 2 * p - 1; outStyle.opacity = p < 0.5 ? 1 - 2 * p : 0; }
    else if (out && kind === 'wipe') inStyle.clipPath = `inset(0 0 0 ${(1 - p) * 100}%)`;
    else if (out && kind === 'slide') { inStyle.transform = `translateX(${(1 - p) * 100}%)`; outStyle.transform = `translateX(${-p * 100}%)`; }
    // Keys and zoom (1.7.0): the crop as a transform, after the slide's translate; the wipe's inset in the element's own coordinates.
    const fa = doc.width / doc.height;
    const inCrop: CropSpec | undefined = needsCrop(inc) ? { framing: inc, frameAspect: fa, pre: out && kind === 'slide' ? `translateX(${(1 - p) * 100}%)` : '', ...(out && kind === 'wipe' ? { wipe: p } : {}) } : undefined;
    const outCrop: CropSpec | undefined = out && needsCrop(out.placed.clip) ? { framing: out.placed.clip, frameAspect: fa, pre: kind === 'slide' ? `translateX(${-p * 100}%)` : '' } : undefined;
    // Picture fades to and from black (0.96.0): brightness, as the export's fade, so they combine with any transition.
    const inLocal = ph - hit.placed.recordIn;
    const fin = vfades[hit.placed.index] ? fadeFactor(vfades[hit.placed.index]!, inLocal) : 1;
    const inFilter = pictureFilter(inc, fin);
    if (inFilter) inStyle.filter = inFilter;
    const sIn = soundFadeFactor(inc, clipDuration(inc), inLocal);
    active.push({ key: k(inc.id), seconds: hit.sourceTicks / S, volume: !heard(inc) ? 0 : levelGain(doc, inc, 'a1', hit.sourceTicks) * (out ? p : 1) * sIn * crossfadeFactor(xp, inc.id, ph), style: inStyle, ...(inCrop ? { crop: inCrop } : {}), ...(inc.fit === 'blur' ? { blur: true } : {}) });
    if (out) {
      const oc = out.placed.clip;
      const outLocal = ph - out.placed.recordIn;
      const fo = vfades[out.placed.index] ? fadeFactor(vfades[out.placed.index]!, outLocal) : 1;
      const outFilter = pictureFilter(oc, fo);
      if (outFilter) outStyle.filter = outFilter;
      const sOut = soundFadeFactor(oc, clipDuration(oc), outLocal);
      active.push({ key: k(oc.id), seconds: out.sourceTicks / S, volume: !heard(oc) ? 0 : levelGain(doc, oc, 'a1', out.sourceTicks) * (1 - p) * sOut, style: outStyle, ...(outCrop ? { crop: outCrop } : {}) });
    }
  }
  for (const l of layersAt(doc, ph)) {
    const c = l.clip;
    // Motion keys (1.7.0): position, size and opacity at the clip's source time, over its static values.
    const m = c.motion ? layerPlaceAt(c, l.sourceTicks) : c;
    const box = m.scale * 100;
    const local = ph - c.recordInTicks;
    // Layer fades to and from transparent (0.96.0).
    const f = fadeFactor(pictureFades(c, doc.frameRate), local);
    active.push({
      key: k(c.id), seconds: l.sourceTicks / S, volume: c.muted || !trackAudible(doc, l.layer.id) ? 0 : levelGain(doc, c, l.layer.id, l.sourceTicks) * soundFadeFactor(c, clipDuration(c), local),
      style: {
        zIndex: 10 + l.layerIndex, opacity: m.opacity * f, width: `${box}%`, height: `${box}%`, left: `${50 + m.x * 100 - box / 2}%`, top: `${50 + m.y * 100 - box / 2}%`,
        ...cssFit(c.fit, c.reframeX, c.reframeY), ...(c.color ? { filter: pictureFilter(c, 1) } : {}),
      },
      ...(needsCrop(c) ? { crop: { framing: c, frameAspect: doc.width / doc.height, pre: '' } } : {}),
    });
  }
  // A2 and (1.7.3) A3–A8: audio-only elements, each track's mute and solo as the export applies them.
  for (const t of allAudioTracks(doc)) {
    if (!trackAudible(doc, t.id)) continue;
    for (const c of t.clips) {
      if (c.muted || ph < c.recordInTicks || ph >= c.recordInTicks + clipDuration(c)) continue;
      const local = ph - c.recordInTicks;
      active.push({ key: k(c.id), seconds: (c.sourceInTicks + local) / S, volume: levelGain(doc, c, t.id, c.sourceInTicks + local) * soundFadeFactor(c, clipDuration(c), local) * crossfadeFactor(xp, c.id, ph), style: {} });
    }
  }
  // 1.7.4: an audio-track crossfade starts the incoming clip early, from its head handle (unless it continues the
  // outgoing clip's element).
  for (const pre of prerollingAt(doc, xp, ph)) {
    // 1.8.0: also an outgoing clip played on past its Out (its partner is then the incoming clip).
    const c = allAudioTracks(doc).find((t) => t.id === pre.track)?.clips.find((q) => q.id === pre.clipId);
    if (!c || c.muted || !trackAudible(doc, pre.track) || k(c.id) === k(pre.partnerId) || active.some((a) => a.key === k(c.id))) continue;
    active.push({ key: k(c.id), seconds: Math.max(0, pre.sourceTicks) / S, volume: levelGain(doc, c, pre.track, pre.sourceTicks) * crossfadeFactor(xp, c.id, ph), style: {} });
  }
  // Clips starting soon get their element now; once within the pre-roll it runs hidden and silent. A clip that
  // continues the one playing uses its element and is not upcoming.
  const found: Array<Upcoming & { start: number }> = [];
  const soon = (start: number) => start > ph && start <= ph + LOOKAHEAD;
  const soonClip = (id: string, start: number, sourceIn: number) => { if (soon(start)) found.push({ key: k(id), seconds: sourceIn / S, dt: (start - ph) / S, start }); };
  for (const p of layoutVideo(doc)) soonClip(p.clip.id, p.recordIn, p.clip.sourceInTicks);
  for (const l of doc.layers ?? []) for (const c of l.clips) if (!l.hidden) soonClip(c.id, c.recordInTicks, c.sourceInTicks);
  for (const t of allAudioTracks(doc)) for (const c of t.clips) if (!c.muted) soonClip(c.id, c.recordInTicks, c.sourceInTicks);
  const shown = new Set(active.map((a) => a.key));
  const upcoming: Upcoming[] = [];
  for (const u of found.sort((a, b) => a.start - b.start)) if (!shown.has(u.key) && !upcoming.some((x) => x.key === u.key)) upcoming.push(u);
  return { active, upcoming };
}

/** The sequence on a bar under the picture (0.96.0): the reused player timeline with the cuts and markers, click or drag to scrub. */
function MiniTimeline({ doc, clock, total, tcAt, onSeek }: { doc: SequenceDocument; clock: PlayheadClock; total: number; tcAt: (s: number) => string; onSeek: (ticks: number) => void }) {
  const ph = useClockValue(clock);
  const cuts = useMemo(() => layoutVideo(doc).slice(1).map((p) => p.recordIn), [doc]);
  const pct = (t: number) => `${total ? (t / total) * 100 : 0}%`;
  return (
    <BarTimeline duration={total / S} time={Math.min(ph, total) / S} buffered={0} range={null} waveformUrl={null} waveformKey={null} spriteUrl={null} spriteLayout={null}
      tcAt={tcAt} onSeek={(s) => onSeek(Math.round(s * S))} marks={{ in: null, out: null }} strip={false} testId="program-mini-timeline">
      {cuts.map((t) => <div key={`c${t}`} className="mini-cut" style={{ left: pct(t) }} />)}
      {(doc.markers ?? []).map((m) => <div key={m.id} className={`mini-marker ${m.color ?? 'red'}`} style={{ left: pct(m.atTicks) }} title={m.label} data-testid="mini-marker" />)}
    </BarTimeline>
  );
}

/**
 * Editing from the monitor (1.7.0, docs/dev/formats-1.7.0-build.md): the editor's apply and seal, whether this person
 * may edit now, and the timeline selection. Without it the monitor is view-only.
 */
export interface ProgramViewerEdit {
  apply: EditorApply;
  seal: () => void;
  canEdit: boolean;
  selectedClipId: string | null;
  select?: (clipId: string) => void;
}

/**
 * The overlays menu's settings (1.7.0), per format id ('primary' included), remembered in this browser. Safe areas are
 * one setting for every format since 1.7.1 (the editor.safeArea preference, the Safe areas button); 1.7.0's per-format
 * `safe` entries are ignored.
 */
interface OverlayPrefs {
  /** The platform safe zone shown in a format (a PLATFORM_SAFE_ZONES id), or none. */
  zone?: Record<string, string | null>;
  /** Guides: a format's outline hidden (false); shown otherwise. */
  guides?: Record<string, boolean>;
  guideOpacity?: number;
}

/** A platform's approximate safe zone on the picture: the covered edges tinted, with its name. */
function SafeZone({ zone }: { zone: PlatformSafeZone }) {
  const i = zone.insets;
  const p = (v: number) => `${(v * 100).toFixed(2)}%`;
  return (
    <div className="platform-zone" data-testid="platform-safe-zone" data-zone={zone.id} aria-hidden>
      <div className="pz-band" style={{ left: 0, right: 0, top: 0, height: p(i.top) }} />
      <div className="pz-band" style={{ left: 0, right: 0, bottom: 0, height: p(i.bottom) }} />
      <div className="pz-band" style={{ left: 0, top: p(i.top), bottom: p(i.bottom), width: p(i.left) }} />
      <div className="pz-band" style={{ right: 0, top: p(i.top), bottom: p(i.bottom), width: p(i.right) }} />
      <div className="pz-safe" style={{ left: p(i.left), right: p(i.right), top: p(i.top), bottom: p(i.bottom) }} />
      <span className="pz-label">{zone.label} · approximate</span>
    </div>
  );
}

export function ProgramViewer({ doc: fullDoc, sources, templates, playhead, setPlayhead, active, onActivate, clock, audio, onPlayingChange, onAddMarker, edit, overlay, hiddenGfx = NO_TRACKS, playRangeRef }: {
  doc: SequenceDocument;
  sources: Map<string, SequenceSource>;
  templates: GraphicTemplateDTO[];
  /** The editor's playhead: the position while paused (during playback the clock carries it). */
  playhead: number;
  setPlayhead: (t: number) => void;
  active: boolean;
  onActivate: () => void;
  /** The live playhead (0.96.0): written on every frame of playback, read by the timeline. */
  clock: PlayheadClock;
  /** The viewer's Web Audio graph and meter (0.96.0). */
  audio: PreviewAudio;
  onPlayingChange?: (playing: boolean) => void;
  /** 1.9.0: set to a function that plays from one time to another and stops there (⌥P plays the selected cue). */
  playRangeRef?: { current: ((fromTicks: number, toTicks: number) => void) | null };
  /** Shown when the person may edit: add a marker at this position. */
  onAddMarker?: (ticks: number) => void;
  /** 1.7.0: editing the framing from the monitor (drag to pan, wheel or pinch to zoom, Guides outlines). */
  edit?: ProgramViewerEdit;
  /** 1.7.2: shown over the picture area (the trim view while a clip is slipped or slid; it renders nothing otherwise). */
  overlay?: ReactNode;
  /** 1.7.3: graphics tracks hidden in the preview only (0-based; the editor's view pref editor.hiddenGfx). */
  hiddenGfx?: number[];
}) {
  // 1.7.0: the active format and view (Editor.tsx provides them). The stage shows one format's document
  // (documentForFormat, as the export renders it): the active one, or the primary in Guides.
  const view = useFormatView(fullDoc);
  const mode = view.formats.length > 1 ? view.mode : 'single';
  const shownFormat: FormatRef = mode === 'guides' ? PRIMARY_FORMAT : view.active;
  const doc = useMemo(() => documentForFormat(fullDoc, shownFormat), [fullDoc, shownFormat]);
  const activeDoc = useMemo(() => documentForFormat(fullDoc, view.active), [fullDoc, view.active]);
  const shownFmt = view.formats.find((f) => f.id === shownFormat) ?? view.formats[0]!;
  /** Shuttle rate: 0 stopped, 1 playing, 2 and 4 faster, −1, −2 and −4 backwards (J/K/L, 0.96.0). */
  const [rate, setRate] = useState(0);
  const playing = rate !== 0;
  const [slots, setSlots] = useState<Slot[]>([]);
  const elements = useRef(new Map<string, HTMLVideoElement | HTMLAudioElement | HTMLImageElement>());
  const total = sequenceDuration(doc);
  const tpf = ticksPerFrame(doc.frameRate);
  const state = useRef({ playhead, last: 0 });
  const playingRef = useRef(false);
  playingRef.current = playing;
  const rateRef = useRef(0);
  rateRef.current = rate;
  // What the picture shows during playback, re-rendered at most ~30 times a second in this viewer only (0.96.0).
  const [livePh, setLivePh] = useState(playhead);
  const shownPh = playing ? livePh : playhead;
  // OGraf graphics follow the playhead on every animation frame of playback (0.93.0).
  const graphicsClock = useRef(new Set<(ph: number) => void>());
  const [seekGen, setSeekGen] = useState(0);
  const [monitorVolume, setMonitorVolume] = usePref('editor.monitor.volume', 1);
  const [muted, setMuted] = usePref('editor.monitor.muted', false);
  const [safeArea, setSafeArea] = usePref('editor.safeArea', false);
  const [overlays, setOverlays] = usePref<OverlayPrefs>('editor.overlays', {});
  const zones = useMemo(() => safeZonesFor(shownFmt), [shownFmt.width, shownFmt.height]); // eslint-disable-line react-hooks/exhaustive-deps
  const zone = zones.find((z) => z.id === overlays.zone?.[shownFormat]) ?? null;
  const guidesShown = view.formats.filter((f) => !f.isPrimary && overlays.guides?.[f.id] !== false).map((f) => f.id);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: PointerEvent) => { if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('pointerdown', close); document.removeEventListener('keydown', esc); };
  }, [menuOpen]);
  // Picture sizes of the sources by version id (from the elements), for crop windows and the Guides outlines.
  const [sourceSizes, setSourceSizes] = useState<Map<string, Size>>(() => new Map());
  const noteSize = useCallback((versionId: string | undefined, el: Element | null) => {
    const size = mediaSize(el);
    if (!versionId || !size) return;
    setSourceSizes((prev) => {
      const cur = prev.get(versionId);
      if (cur && cur.width === size.width && cur.height === size.height) return prev;
      const next = new Map(prev);
      next.set(versionId, size);
      return next;
    });
  }, []);
  // Listeners for a new frame on an element while paused (seeked, loaded): the Blur background and Side by side redraw.
  const frameListeners = useRef(new Set<() => void>());
  const blurCanvas = useRef<HTMLCanvasElement>(null);
  /** The element of the Blur fit clip now (its background is drawn from it), set by sync. */
  const blurFrom = useRef<string | null>(null);
  // rVFC (1.7.0): the media time of the frame each element presented last, while playing; and pending callbacks.
  const frameTimes = useRef(new Map<string, { t: number; at: number }>());
  const frameCallbacks = useRef(new Map<string, { el: HTMLVideoElement; id: number }>());
  /** The latest active entries by element key, for the frame callbacks. */
  const activeByKey = useRef(new Map<string, Active>());
  const rootRef = useRef<HTMLDivElement>(null);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => { audio.setMonitor(monitorVolume, muted); }, [audio, monitorVolume, muted]);
  useEffect(() => { onPlayingChange?.(playing); }, [playing, onPlayingChange]);
  useEffect(() => {
    const onFs = () => setFullscreen(document.fullscreenElement === rootRef.current);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  // Clips that continue each other share one element, keyed by the first clip's id.
  const heads = useMemo(() => elementHeads(doc), [doc]);
  const vfades = useMemo(() => storylinePictureFades(doc), [doc]);
  const xplan = useMemo(() => crossfadePlan(doc, sourceLengths(sources.values())), [doc, sources]);
  // Every element key (and clip id) → what kind of element shows it, and from which proxy.
  const clipMedia = useMemo(() => {
    const m = new Map<string, Slot>();
    const add = (id: string, versionId: string, audioOnly: boolean) => {
      const src = sources.get(versionId);
      // A recording (1.0.0-rc.1) plays from its snapshot until its own proxy is made, then from the proxy.
      const url = src?.proxyUrl ?? src?.live?.playlistUrl;
      if (!src || !url) return;
      m.set(id, { key: heads.get(id) ?? id, kind: audioOnly ? 'audio' : src.mediaKind === 'image' ? 'image' : 'video', url, versionId, ...(src.proxyUrl ? {} : { live: true }) });
    };
    for (const c of doc.video) add(c.id, c.versionId, false);
    for (const l of doc.layers ?? []) for (const c of l.clips) add(c.id, c.versionId, false);
    for (const t of allAudioTracks(doc)) for (const c of t.clips) add(c.id, c.versionId, true);
    return m;
  }, [doc, sources, heads]);

  // How far into its recording each live element must play (seconds): the furthest out point of its clips.
  const liveNeed = useMemo(() => {
    const need = new Map<string, number>();
    for (const { clip } of allClips(doc)) {
      const slot = clipMedia.get(clip.id);
      if (slot?.live) need.set(slot.key, Math.max(need.get(slot.key) ?? 0, clip.sourceOutTicks / S));
    }
    return need;
  }, [doc, clipMedia]);

  /**
   * Bring every element in line with a position: style, volume, time; play or pause.
   * `hold`: playback is starting and the clock waits for the first frame; elements are started
   * exactly at the position and nothing is pre-rolled yet.
   */
  const sync = useCallback((ph: number, isPlaying: boolean, hold = false) => {
    const { active: now, upcoming } = frameAt(doc, heads, ph, vfades, xplan);
    const wanted = [...new Set([...now.map((a) => a.key), ...upcoming.map((u) => u.key)])].filter((k) => clipMedia.has(k));
    setSlots((prev) => {
      // Keep the order of the elements already there: moving a media element in the DOM pauses it.
      const keep = prev.filter((s) => wanted.includes(s.key) && s.url === clipMedia.get(s.key)!.url);
      const next = [...keep, ...wanted.filter((k) => !keep.some((s) => s.key === k)).map((k) => clipMedia.get(k)!)];
      return next.length === prev.length && next.every((s, n) => s === prev[n] || (s.key === prev[n]!.key && s.url === prev[n]!.url)) ? prev : next;
    });
    // Faster shuttle forwards plays the elements faster (0.96.0); everything else at 1×.
    const speed = isPlaying && rateRef.current > 1 ? rateRef.current : 1;
    const shown = new Set(now.map((a) => a.key));
    activeByKey.current = new Map(now.map((a) => [a.key, a]));
    let blurKey: string | null = null;
    for (const a of now) {
      const el = elements.current.get(a.key);
      if (!el) continue;
      if (!(el instanceof HTMLAudioElement)) {
        const props: Record<string, string> = { ...STYLE_RESET, ...Object.fromEntries(Object.entries(a.style).map(([k, v]) => [k, String(v)])) };
        if (a.crop) {
          // While playing, the time of the frame on screen (rVFC), so the crop matches it; paused, the position's.
          const ft = frameTimes.current.get(a.key);
          const t = isPlaying && el instanceof HTMLVideoElement ? (ft && performance.now() - ft.at < 250 ? ft.t : el.currentTime) : a.seconds;
          Object.assign(props, cropStyle(el, a.crop, t) ?? {});
          if (isPlaying && el instanceof HTMLVideoElement && a.crop.framing.keys?.length) watchFrames(a.key, el);
        }
        setStyles(el, props);
        if (a.blur) blurKey = a.key;
      }
      if (el instanceof HTMLMediaElement) {
        if (el.playbackRate !== speed) el.playbackRate = speed;
        setVolume(el, a.volume);
        if (hold && isPlaying) startHeld(el, a.seconds);
        else followClock(el, a.seconds, isPlaying);
      }
    }
    const lead = currentLead();
    for (const [key, el] of elements.current) {
      if (shown.has(key)) continue;
      if (!(el instanceof HTMLAudioElement)) setStyles(el, { visibility: 'hidden' });
      if (el instanceof HTMLMediaElement) {
        const up = upcoming.find((u) => u.key === key);
        const plan = up ? planUpcoming({ playing: isPlaying && !hold, sourceIn: up.seconds, dt: up.dt, lead }) : null;
        if (plan?.kind === 'run') {
          // Pre-roll: already running (hidden, silent) when its cut comes.
          if (el.playbackRate !== speed) el.playbackRate = speed;
          setVolume(el, PREROLL_VOLUME);
          followClock(el, plan.target, true);
          continue;
        }
        if (!el.paused) el.pause();
        if (plan && Math.abs(el.currentTime - plan.to) > 0.05) el.currentTime = plan.to;
      }
    }
    blurFrom.current = blurKey;
    drawBlur();
  }, [doc, heads, clipMedia, vfades, xplan]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * The Blur fit's background (1.7.0): the clip's picture drawn small on a canvas behind the fitted picture, blurred
   * and scaled up by CSS; hidden when the clip on screen has no Blur fit.
   */
  const drawBlur = () => {
    const c = blurCanvas.current;
    if (!c) return;
    const key = blurFrom.current;
    const el = key ? elements.current.get(key) : undefined;
    const a = key ? activeByKey.current.get(key) : undefined;
    const size = mediaSize(el);
    if (!key || !el || el instanceof HTMLAudioElement || !size || !a) { setStyles(c, { visibility: 'hidden' }); return; }
    const w = 48, h = Math.max(1, Math.round(48 / (doc.width / doc.height)));
    if (c.width !== w) c.width = w;
    if (c.height !== h) c.height = h;
    // A seeking element keeps the last background drawn (1.7.1: drawing it mid-seek could show a stale or empty frame).
    const ready = el instanceof HTMLVideoElement ? el.readyState >= 2 && !el.seeking : el.complete;
    if (ready) {
      const r = coverSource(size, w / h);
      try { c.getContext('2d')?.drawImage(el, r.sx, r.sy, r.sw, r.sh, 0, 0, w, h); } catch { /* not decodable yet */ }
    }
    // The export's sigma (BLUR_FIT_SIGMA of the short side), in the canvas's displayed (CSS) pixels.
    const sigma = Math.max(1, BLUR_FIT_SIGMA * Math.min(c.clientWidth || 0, c.clientHeight || 0));
    setStyles(c, { visibility: 'visible', opacity: String(a.style.opacity ?? 1), filter: [`blur(${sigma.toFixed(2)}px)`, String(a.style.filter ?? '')].filter(Boolean).join(' ') });
  };

  /** The transform crop of an element (1.7.0) at a source second, or null while its picture size is unknown. */
  const cropStyle = (el: HTMLElement, crop: CropSpec, seconds: number): Record<string, string> | null => {
    const size = mediaSize(el);
    if (!size) return null;
    const win = cropAt(size.width / size.height, crop.frameAspect, crop.framing, seconds * S);
    if (!win) return null;
    return { objectFit: 'fill', objectPosition: '', transformOrigin: '0 0', transform: cropTransform(win, crop.pre), clipPath: cropClipPath(win, crop.wipe) };
  };

  /** Follow an element's presented frames while playing (requestVideoFrameCallback), re-applying its keyed crop at each. */
  const watchFrames = (key: string, el: HTMLVideoElement) => {
    if (frameCallbacks.current.has(key) || typeof el.requestVideoFrameCallback !== 'function') return;
    const id = el.requestVideoFrameCallback((_now, meta) => {
      frameCallbacks.current.delete(key);
      frameTimes.current.set(key, { t: meta.mediaTime, at: performance.now() });
      const a = activeByKey.current.get(key);
      if (!a?.crop || !playingRef.current || elements.current.get(key) !== el) return;
      const st = cropStyle(el, a.crop, meta.mediaTime);
      if (st) setStyles(el, st);
      if (blurFrom.current === key) drawBlur();
      watchFrames(key, el);
    });
    frameCallbacks.current.set(key, { el, id });
  };
  const stopWatchingFrames = () => {
    for (const { el, id } of frameCallbacks.current.values()) el.cancelVideoFrameCallback?.(id);
    frameCallbacks.current.clear();
    frameTimes.current.clear();
  };
  /** A media element has a new frame while paused: redraw what is drawn from it. */
  const onFrameReady = () => {
    if (playingRef.current) return;
    drawBlur();
    for (const f of frameListeners.current) f();
  };

  const onMetadata = useCallback(() => { if (!playingRef.current) sync(state.current.playhead, false); }, [sync]);

  // While paused: show the frame under the playhead.
  useEffect(() => {
    if (!playing) sync(playhead, false);
  }, [playhead, playing, sync, slots]);

  /**
   * The last position this viewer and the editor agreed on. While playing the editor's playhead is not updated
   * (the clock is), so the prop is stale then; a prop that differs from this is a move made in the editor (the
   * ruler, a marker, the timecode field): the live position follows it, and playback plays on from there. Our own
   * stop position coming back as the prop is not a move. (Not read and written during render: StrictMode renders
   * twice.)
   */
  const committed = useRef(playhead);
  useEffect(() => {
    if (playhead === committed.current) return;
    committed.current = playhead;
    const moved = Math.abs(playhead - state.current.playhead) > tpf / 2;
    state.current.playhead = playhead;
    if (playingRef.current && moved) setSeekGen((g) => g + 1);
  }, [playhead, tpf]);

  /** Playback stops at `ph`: the editor's playhead becomes the position. */
  const stopAt = useCallback((ph: number) => {
    state.current.playhead = ph;
    committed.current = Math.max(0, Math.min(ph, total));
    clock.set(ph);
    setPlayhead(ph);
  }, [clock, setPlayhead, total]);

  // Playback: the wall clock drives the playhead; elements follow it. The clock starts when the main
  // picture has really presented its first frame (Safari needs ~0.3-0.5 s), at that frame's position.
  useEffect(() => {
    if (rate !== 1) return;
    let raf = 0;
    let wallStart = 0;
    const base = state.current.playhead >= total ? 0 : state.current.playhead;
    let clockBase = base;
    let running = false;
    const holdStart = performance.now();
    const cancels: Array<() => void> = [];
    const first = frameAt(doc, heads, base, vfades).active.find((a) => clipMedia.get(a.key)?.kind !== 'audio');
    const mainEl = first && clipMedia.get(first.key)?.kind === 'video' ? elements.current.get(first.key) : undefined;
    const startClock = (mediaTime: number | null, measured: boolean) => {
      if (running) return;
      running = true;
      for (const c of cancels) c();
      wallStart = performance.now();
      audio.startMetering();
      if (first && mediaTime !== null) clockBase = clockBaseFromFrame({ base, mainSeconds: first.seconds, mediaTime, ticksPerSecond: S });
      if (measured) noteStartLatency((wallStart - holdStart) / 1000);
    };
    const tick = () => {
      if (!running) { sync(base, true, true); raf = requestAnimationFrame(tick); return; }
      const ph = clockBase + Math.round(((performance.now() - wallStart) / 1000) * S);
      if (ph >= total) { stopAt(total); setRate(0); return; }
      sync(ph, true);
      for (const onFrame of graphicsClock.current) onFrame(ph);
      state.current.playhead = ph;
      clock.set(ph);
      audio.analyse();
      if (performance.now() - state.current.last > 33) { state.current.last = performance.now(); setLivePh(ph); }
      raf = requestAnimationFrame(tick);
    };
    if (mainEl instanceof HTMLVideoElement && first) {
      const v: HTMLVideoElement = mainEl;
      sync(base, true, true);
      const timer = setTimeout(() => startClock(v.currentTime, false), FIRST_FRAME_TIMEOUT_MS);
      cancels.push(() => clearTimeout(timer));
      if (typeof v.requestVideoFrameCallback === 'function') {
        const id = v.requestVideoFrameCallback((_n, meta) => startClock(meta.mediaTime, true));
        cancels.push(() => v.cancelVideoFrameCallback(id));
      } else {
        const onPlaying = () => { const t = setTimeout(() => startClock(v.currentTime, true), 120); cancels.push(() => clearTimeout(t)); };
        (v as HTMLVideoElement).addEventListener('playing', onPlaying, { once: true });
        cancels.push(() => (v as HTMLVideoElement).removeEventListener('playing', onPlaying));
      }
    } else {
      running = true;
      wallStart = performance.now();
      audio.startMetering();
    }
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      for (const c of cancels) c();
      stopWatchingFrames();
      for (const el of elements.current.values()) if (el instanceof HTMLMediaElement) el.pause();
      if (running) stopAt(state.current.playhead);
    };
  }, [rate, total, sync, stopAt, doc, heads, clipMedia, vfades, audio, clock, seekGen]);

  // Shuttle at other speeds (J/K/L, 0.96.0): no sound; forwards the elements play faster, backwards they are
  // stepped (paused, seeked) as fast as they can show frames.
  useEffect(() => {
    if (rate === 0 || rate === 1) return;
    let raf = 0;
    const wallStart = performance.now();
    const base = state.current.playhead;
    audio.setProgramMuted(true);
    const tick = () => {
      const ph = Math.max(0, Math.min(total, base + Math.round(((performance.now() - wallStart) / 1000) * S * rate)));
      const busy = [...elements.current.values()].some((el) => el instanceof HTMLMediaElement && el.seeking);
      if (rate > 0) sync(ph, true);
      else if (!busy) sync(ph, false);
      for (const onFrame of graphicsClock.current) onFrame(ph);
      state.current.playhead = ph;
      clock.set(ph);
      if (performance.now() - state.current.last > 33) { state.current.last = performance.now(); setLivePh(ph); }
      if ((rate > 0 && ph >= total) || (rate < 0 && ph <= 0)) { setRate(0); return; }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      stopWatchingFrames();
      audio.setProgramMuted(false);
      for (const el of elements.current.values()) if (el instanceof HTMLMediaElement) { el.pause(); el.playbackRate = 1; }
      stopAt(state.current.playhead);
    };
  }, [rate, total, sync, stopAt, audio, clock, seekGen]);

  const play = useCallback((next: number | ((r: number) => number)) => {
    // Web Audio starts on a user gesture (this click or key press).
    audio.resume();
    const cur = rateRef.current;
    const v = typeof next === 'function' ? next(cur) : next;
    if (v !== 0 && cur === 0) { setLivePh(state.current.playhead); state.current.last = performance.now(); }
    rateRef.current = v;
    setRate(v);
  }, [audio]);
  /** Frame stepping and jumps: from the live position (a held arrow key repeats faster than React renders). */
  const jump = useCallback((to: number) => {
    setRate(0);
    const t = Math.max(0, Math.min(total, to));
    state.current.playhead = t;
    clock.set(t);
    setPlayhead(t);
  }, [total, clock, setPlayhead]);

  // 1.9.0: play a range (a cue) and stop at its end; pressing play or stop in between ends it.
  const rangeStop = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (!playRangeRef) return;
    playRangeRef.current = (from, to) => {
      rangeStop.current?.();
      jump(from);
      let started = false;
      const off = clock.subscribe((t) => {
        if (rateRef.current === 0 && started) { off(); rangeStop.current = null; return; }
        started = true;
        if (t >= to - tpf / 2) { off(); rangeStop.current = null; play(0); jump(Math.max(from, to - tpf)); }
      });
      rangeStop.current = off;
      requestAnimationFrame(() => play(1));
    };
    return () => { rangeStop.current?.(); playRangeRef.current = null; };
  }, [playRangeRef, jump, play, clock, tpf]);

  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e, 'input, textarea, select, [contenteditable]') || e.metaKey || e.ctrlKey) return;
      const k = e.key.toLowerCase();
      const live = clock.get();
      if (e.key === ' ') { e.preventDefault(); play((r) => (r === 0 ? 1 : 0)); }
      else if (k === 'k' || k === 'j' || k === 'l') { e.preventDefault(); play((r) => nextShuttle(r, k)); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); jump(live + (e.shiftKey ? S : tpf)); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); jump(live - (e.shiftKey ? S : tpf)); }
      else if (e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        // Previous / next marker (0.96.0).
        e.preventDefault();
        const marks = (doc.markers ?? []).map((m) => m.atTicks);
        const to = e.key === 'ArrowUp' ? [...marks].reverse().find((t) => t < live - tpf / 2) : marks.find((t) => t > live + tpf / 2);
        if (to !== undefined) jump(to);
      }
      else if (e.key === 'Home') jump(0);
      else if (e.key === 'End') jump(total);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, total, tpf, play, jump, clock, doc.markers]);

  // One stable ref callback per clip, so React does not detach and re-attach the elements on every render.
  // Media elements are routed through Web Audio once, when they are attached (0.96.0).
  const refs = useRef(new Map<string, (el: HTMLVideoElement | HTMLAudioElement | HTMLImageElement | null) => void>());
  const ref = (key: string) => {
    let cb = refs.current.get(key);
    if (!cb) {
      cb = (el) => {
        if (el) {
          elements.current.set(key, el);
          if (el instanceof HTMLMediaElement) audio.attach(el);
        } else {
          const old = elements.current.get(key);
          if (old instanceof HTMLMediaElement) audio.detach(old);
          elements.current.delete(key);
        }
      };
      refs.current.set(key, cb);
    }
    return cb;
  };
  const [stageEl, setStageEl] = useState<HTMLDivElement | null>(null);
  const [pictureEl, setPictureEl] = useState<HTMLDivElement | null>(null);
  // The stage is the sequence frame's shape inside the pane (1.0.0-rc.11: portrait sequences are tall boxes); 1.7.0:
  // the shown format's shape.
  const frameSize = useMemo(() => ({ width: doc.width, height: doc.height }), [doc.width, doc.height]);
  const fitted = useFittedBox(pictureEl, frameSize);
  // Side by side (1.7.0): every format at one height across the picture area; the stage is the active format's panel.
  const area = useElementSize(mode === 'side' ? pictureEl : null);
  const sideSizes = useMemo(() => (mode === 'side' && area ? sideBySideSizes(view.formats, { width: area.width - 16, height: area.height - 16 }, 8) : null), [mode, area, view.formats]);
  const stageBox = sideSizes ? sideSizes[shownFmt.index] ?? null : fitted;
  const picture = useVideoContentRect(stageEl, frameSize);
  const tcAt = (ticks: number) => formatTimecode(framesToTimecode(ticksToFrames(Math.min(ticks, Math.max(0, total - 1)), doc.frameRate), doc.frameRate));
  const tc = tcAt(shownPh);
  const hasPicture = doc.video.length > 0 || (doc.layers ?? []).some((l) => l.clips.length);
  const toggleFullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    else void rootRef.current?.requestFullscreen().catch(() => undefined);
  };
  const slotOf = (key: string) => slots.find((x) => x.key === key);
  const onLoaded = (key: string) => (e?: { currentTarget: Element }) => {
    noteSize(slotOf(key)?.versionId, e?.currentTarget ?? elements.current.get(key) ?? null);
    onMetadata();
    onFrameReady();
  };
  /** The monitor's picture elements by clip id (Side by side draws from them). */
  const pictureElements = useCallback(() => {
    const m = new Map<string, PictureElement>();
    for (const [id, slot] of clipMedia) {
      const el = elements.current.get(slot.key);
      if (el instanceof HTMLVideoElement || el instanceof HTMLImageElement) m.set(id, el);
    }
    return m;
  }, [clipMedia]);

  /*
   * Direct manipulation (1.7.0, single view, with edit.canEdit): drag the picture to pan, the wheel or a pinch to zoom,
   * on the selected clip or the V1 clip at the playhead, in the active format (monitor-edit.ts framingEdit: the key at
   * the playhead, a new key, or the static framing; one undo step per gesture, sealed on release).
   */
  const editable = mode === 'single' && Boolean(edit?.canEdit);
  const live = useRef({ doc, fullDoc, edit, sourceSizes, active: view.active, stage: stageBox });
  live.current = { doc, fullDoc, edit, sourceSizes, active: view.active, stage: stageBox };
  const grabTarget = useCallback(() => {
    const { doc: d, edit: ed, sourceSizes: sizes } = live.current;
    const target = editTargetAt(d, clock.get(), ed?.selectedClipId);
    if (!target) return null;
    const clip = target.layer ? (d.layers ?? []).flatMap((l) => l.clips).find((c) => c.id === target.clipId) : d.video.find((c) => c.id === target.clipId);
    const size = sizes.get(target.versionId);
    if (!clip || !size) return null;
    const sa = size.width / size.height, fa = d.width / d.height;
    const win = cropAt(sa, fa, clip, target.sourceTicks);
    if (!win) return null;
    const boxScale = target.layer ? layerPlaceAt(clip as Parameters<typeof layerPlaceAt>[0], target.sourceTicks).scale : 1;
    return { target, win, zoom: framingAt(clip, target.sourceTicks).zoom, sa, fa, boxScale };
  }, [clock]);
  type Grab = NonNullable<ReturnType<typeof grabTarget>>;
  const setFraming = useCallback((g: Grab, next: { x: number; y: number; zoom: number }) => {
    const { fullDoc: d, edit: ed, active: fid } = live.current;
    const e = ed && framingEdit(d, g.target.clipId, fid, g.target.sourceTicks, next);
    if (e && ed) ed.apply(e.fn, e.coalesce);
  }, []);
  const gesture = useRef<null | { g: Grab; x0: number; y0: number; moved: boolean; pointers: Map<number, { x: number; y: number }>; d0: number }>(null);
  const onStageDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!editable || (e.pointerType === 'mouse' && e.button !== 0)) return;
    const cur = gesture.current;
    if (cur) {
      // A second finger: a pinch from here.
      cur.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (cur.pointers.size === 2) { const [a, b] = [...cur.pointers.values()]; cur.d0 = Math.hypot(a!.x - b!.x, a!.y - b!.y); cur.g = grabTarget() ?? cur.g; }
      e.currentTarget.setPointerCapture?.(e.pointerId);
      return;
    }
    const g = grabTarget();
    if (!g) return;
    gesture.current = { g, x0: e.clientX, y0: e.clientY, moved: false, pointers: new Map([[e.pointerId, { x: e.clientX, y: e.clientY }]]), d0: 0 };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onStageMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const cur = gesture.current;
    const box = live.current.stage;
    if (!cur || !box || !cur.pointers.has(e.pointerId)) return;
    cur.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (cur.pointers.size === 2 && cur.d0 > 0) {
      const [a, b] = [...cur.pointers.values()];
      const zoom = zoomByPinch(cur.g.zoom, cur.d0, Math.hypot(a!.x - b!.x, a!.y - b!.y));
      const ws = windowSize(cur.g.sa, cur.g.fa, zoom);
      cur.moved = true;
      setFraming(cur.g, { ...panKeepingCentre(cur.g.win, ws.w, ws.h), zoom });
      return;
    }
    if (!cur.moved && Math.hypot(e.clientX - cur.x0, e.clientY - cur.y0) < 3) return;
    cur.moved = true;
    const pan = panByPictureDrag(cur.g.win, (e.clientX - cur.x0) / (box.width * cur.g.boxScale), (e.clientY - cur.y0) / (box.height * cur.g.boxScale));
    setFraming(cur.g, { ...pan, zoom: cur.g.zoom });
  };
  const onStageUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    const cur = gesture.current;
    if (!cur) return;
    cur.pointers.delete(e.pointerId);
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    if (cur.pointers.size) return;
    gesture.current = null;
    if (cur.moved) live.current.edit?.seal();
  };
  useEffect(() => {
    if (!stageEl || !editable) return;
    let sealTimer: ReturnType<typeof setTimeout> | undefined;
    const onWheel = (e: WheelEvent) => {
      const g = grabTarget();
      if (!g) return;
      e.preventDefault();
      const zoom = zoomByWheel(g.zoom, e.deltaY, e.ctrlKey);
      if (zoom === g.zoom) return;
      const ws = windowSize(g.sa, g.fa, zoom);
      setFraming(g, { ...panKeepingCentre(g.win, ws.w, ws.h), zoom });
      clearTimeout(sealTimer);
      sealTimer = setTimeout(() => { sealTimer = undefined; live.current.edit?.seal(); }, 500);
    };
    stageEl.addEventListener('wheel', onWheel, { passive: false });
    return () => { stageEl.removeEventListener('wheel', onWheel); if (sealTimer) { clearTimeout(sealTimer); live.current.edit?.seal(); } };
  }, [stageEl, editable, grabTarget, setFraming]);

  /*
   * Moving the subtitle line (1.7.1): with edit.canEdit, outside Guides, the line on the stage is dragged up or down to
   * set the shown format's subtitle height (one undo step, sealed on release); a double-click sets the default.
   */
  const subsEditable = Boolean(edit?.canEdit) && mode !== 'guides';
  const [subDrag, setSubDrag] = useState<null | { y0: number; b0: number; b: number }>(null);
  // 1.9.0: the line drag sets subtitleBottom, which places bottom-row cues only; a top or middle cue shows a hint.
  const [subHint, setSubHint] = useState(false);
  useEffect(() => { if (!subHint) return; const t = window.setTimeout(() => setSubHint(false), 2500); return () => window.clearTimeout(t); }, [subHint]);
  const endSubDrag = (e: ReactPointerEvent<HTMLSpanElement>) => {
    if (!subDrag) return;
    e.stopPropagation();
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    setSubDrag(null);
    edit?.seal();
  };
  const subtitleDrag = {
    onPointerDown: (e: ReactPointerEvent<HTMLSpanElement>) => {
      if (!picture || (e.pointerType === 'mouse' && e.button !== 0)) return;
      // Not the stage's pan drag.
      e.stopPropagation();
      onActivate();
      e.currentTarget.setPointerCapture?.(e.pointerId);
      const b0 = captionLayout(doc.width, doc.height, doc.subtitleBottom).bottom;
      setSubDrag({ y0: e.clientY, b0, b: b0 });
    },
    onPointerMove: (e: ReactPointerEvent<HTMLSpanElement>) => {
      if (!subDrag || !picture || !edit) return;
      e.stopPropagation();
      const b = Math.min(0.5, Math.max(0.02, Math.round((subDrag.b0 - (e.clientY - subDrag.y0) / picture.height) * 200) / 200));
      if (b === subDrag.b) return;
      setSubDrag({ ...subDrag, b });
      edit.apply((d) => ops.updateFormat(d, shownFormat, { subtitleBottom: b }), subtitleCoalesce(shownFormat));
    },
    onPointerUp: endSubDrag,
    onPointerCancel: endSubDrag,
    onDoubleClick: (e: ReactMouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();
      edit?.apply((d) => ops.updateFormat(d, shownFormat, { subtitleBottom: null }), subtitleCoalesce(shownFormat));
      edit?.seal();
    },
  };

  const graphicsFor = (d: SequenceDocument) => <GraphicLayers doc={d} playhead={shownPh} templates={templates} clock={graphicsClock.current} hidden={hiddenGfx} />;
  const stageStyle: CSSProperties = stageBox
    ? { aspectRatio: `${doc.width} / ${doc.height}`, width: stageBox.width, height: stageBox.height, flex: 'none' }
    : { aspectRatio: `${doc.width} / ${doc.height}` };
  if (mode === 'side') stageStyle.order = shownFmt.index;
  if (editable) stageStyle.touchAction = 'none';
  const setZone = (id: string | null) => setOverlays({ ...overlays, zone: { ...overlays.zone, [shownFormat]: id } });
  const overlayMenu = (
    <div className="menu-anchor overlays-anchor" ref={menuRef}>
      <button type="button" className={`btn ghost icon sm ${zone ? 'on' : ''}`} aria-haspopup="dialog" aria-expanded={menuOpen} aria-label="Overlays"
        title="Overlays: platform safe zones, guides" onClick={() => setMenuOpen(!menuOpen)} data-testid="overlays-toggle"><Layers size={15} /></button>
      {menuOpen && (
        <div className="popover overlays-menu" role="dialog" aria-label="Overlays" data-testid="overlays-menu">
          <p className="section-label">{shownFmt.label}{shownFmt.isPrimary && view.formats.length > 1 ? ' (primary)' : ''}</p>
          {zones.length > 0 && (
            <fieldset className="overlay-group">
              <legend>Platform safe zones <span className="faint">approximate</span></legend>
              <label className="overlay-row"><input type="radio" name="pz" checked={!zone} onChange={() => setZone(null)} data-testid="safe-zone-off" /> Off</label>
              {zones.map((z) => (
                <label key={z.id} className="overlay-row"><input type="radio" name="pz" checked={zone?.id === z.id} onChange={() => setZone(z.id)} data-testid={`safe-zone-${z.id}`} /> {z.label}</label>
              ))}
              <p className="faint overlay-note">Typical areas the apps' buttons and captions cover (checked {zones[0]!.checked}); they vary by app version and device.</p>
            </fieldset>
          )}
          {view.formats.length > 1 && (
            <fieldset className="overlay-group">
              <legend>Guides</legend>
              {view.formats.filter((f) => !f.isPrimary).map((f) => (
                <label key={f.id} className="overlay-row"><input type="checkbox" checked={overlays.guides?.[f.id] !== false} onChange={(e) => setOverlays({ ...overlays, guides: { ...overlays.guides, [f.id]: e.target.checked } })} data-testid={`guide-toggle-${f.id}`} /> {f.label}</label>
              ))}
              <label className="overlay-row">Opacity
                <input type="range" min={0.2} max={1} step={0.05} value={overlays.guideOpacity ?? 1} onChange={(e) => setOverlays({ ...overlays, guideOpacity: Number(e.target.value) })} aria-label="Guides opacity" data-testid="guide-opacity" /></label>
            </fieldset>
          )}
        </div>
      )}
    </div>
  );
  return (
    <div className={`viewer ${active ? 'active' : ''} ${fullscreen ? 'fullscreen' : ''}`} onPointerDown={onActivate} data-testid="program-viewer" ref={rootRef}>
      <div className="viewer-head"><span className="pane-title">Sequence</span><span className="pill" title="Browser preview uses proxies and approximate timing; exports render exact frames from originals">Preview</span><span style={{ flex: 1 }} /><PaneControls /></div>
      <FormatBar formats={view.formats} active={view.active} mode={mode} onActive={view.setActive} onMode={view.setMode} />
      <ColorFilters doc={doc} />
      <div className={`viewer-picture ${mode === 'side' ? 'side' : ''}`} data-testid="program-picture" data-format={shownFormat} data-mode={mode} onDoubleClick={toggleFullscreen} ref={setPictureEl}>
        <div className={`viewer-stage program-stage ${editable ? 'editable' : ''}`} ref={setStageEl} data-testid="program-stage" data-format={shownFormat}
          style={stageStyle} onPointerDown={onStageDown} onPointerMove={onStageMove} onPointerUp={onStageUp} onPointerCancel={onStageUp}>
          <canvas ref={blurCanvas} className="blur-fit-canvas" width={48} height={27} style={{ visibility: 'hidden' }} data-testid="blur-fit-canvas" aria-hidden />
          {slots.map((s) => s.live && s.kind !== 'image'
            ? <LiveMedia key={`${s.key}~live`} kind={s.kind} url={s.url} clipKey={s.key} mediaRef={ref(s.key)} onLoadedMetadata={onLoaded(s.key)} needSeconds={liveNeed.get(s.key) ?? 0} />
            : s.kind === 'audio'
            ? <audio key={s.key} ref={ref(s.key)} src={s.url} preload="auto" crossOrigin="anonymous" />
            : s.kind === 'image'
              ? <img key={s.key} ref={ref(s.key)} src={s.url} alt="" className="program-layer" style={{ visibility: 'hidden' }} data-clip={s.key} onLoad={onLoaded(s.key)} />
              : <video key={s.key} ref={ref(s.key)} src={s.url} className="program-layer" playsInline preload="auto" crossOrigin="anonymous" style={{ visibility: 'hidden' }} data-clip={s.key}
                  onLoadedMetadata={onLoaded(s.key)} onSeeked={onFrameReady} onLoadedData={onFrameReady} />)}
          {/* Graphics sit on the picture, not on the stage around it (the stage letterboxes the picture when the pane's
              shape differs), so they are where the export puts them (0.93.1). */}
          <div className="viewer-overlays" style={picture ? { left: picture.left, top: picture.top, width: picture.width, height: picture.height, right: 'auto', bottom: 'auto' } : undefined}>
            {mode === 'guides' ? (
              <>
                {view.active === PRIMARY_FORMAT && graphicsFor(doc)}
                <FormatGuides doc={fullDoc} formats={view.formats} active={view.active} playhead={shownPh} stage={picture ? { width: picture.width, height: picture.height } : frameSize}
                  sourceSizes={sourceSizes} shown={guidesShown} opacity={overlays.guideOpacity ?? 1} canEdit={Boolean(edit?.canEdit)} apply={edit?.apply ?? noApply} seal={edit?.seal}
                  onActive={view.setActive} clock={graphicsClock.current} activeGraphics={view.active !== PRIMARY_FORMAT ? graphicsFor(activeDoc) : undefined} />
                <span className="guides-graphics-label" data-testid="guides-graphics-label">Graphics: {view.activeFormat.label}</span>
              </>
            ) : graphicsFor(doc)}
            {safeArea && <div className="safe-area" data-testid="safe-area" aria-hidden><div className="safe-action" /><div className="safe-title" /></div>}
            {zone && <SafeZone zone={zone} />}
            {mode === 'side' && <span className="sbs-label" style={{ background: formatColor(shownFmt.index) }}>{shownFmt.label}</span>}
          </div>
          {doc.subtitles && (
            <CueOverlay cues={doc.subtitles.cues} trackStyle={doc.subtitles.style} ticks={shownPh} rect={picture} subtitleBottom={doc.subtitleBottom}
              className={subsEditable ? 'draggable' : ''} testId="program-subtitle"
              lineProps={(_c, l) => !subsEditable ? {} : l.row === 'b'
                ? { ...subtitleDrag, title: 'Drag up or down to move the subtitles in this format; double-click for the default' }
                : { title: 'A top or middle cue: move it with the placement grid in the Subtitles tab', onPointerDown: (e) => { e.stopPropagation(); setSubHint(true); } }}>
              {subDrag && <span className="subtitle-readout" data-testid="subtitle-readout">{subtitlePct(subDrag.b)} % above the bottom</span>}
              {!subDrag && subHint && <span className="subtitle-readout" data-testid="subtitle-hint">Top and middle cues move with the placement grid</span>}
            </CueOverlay>
          )}
          {!hasPicture && <div className="viewer-empty">Add clips from the bin to start the story</div>}
        </div>
        {mode === 'side' && sideSizes && (
          <SideBySide doc={fullDoc} formats={view.formats} playhead={shownPh} playing={playing} videos={pictureElements} sourceSizes={sourceSizes} templates={templates}
            activeId={shownFormat} sizes={sideSizes} clock={graphicsClock.current} frames={frameListeners.current} graphics={graphicsFor} safeArea={safeArea} />
        )}
        {overlay}
      </div>
      <div className="transport viewer-transport" data-testid="program-transport">
        <MiniTimeline doc={doc} clock={clock} total={total} tcAt={(sec) => tcAt(Math.round(sec * S))} onSeek={(t) => jump(t)} />
        <div className="controls">
          <button className="btn ghost icon" aria-label="Previous frame" title="Previous frame (←)" onClick={() => jump(clock.get() - tpf)}><ChevronLeft size={18} /></button>
          <button className="btn icon" aria-label={playing ? 'Pause sequence' : 'Play sequence'} title="Play/pause (Space) · J/K/L shuttle" onClick={() => play((r) => (r === 0 ? 1 : 0))} disabled={!total} data-testid="program-play">{playing ? <Pause size={16} /> : <Play size={16} />}</button>
          <button className="btn ghost icon" aria-label="Next frame" title="Next frame (→)" onClick={() => jump(clock.get() + tpf)}><ChevronRight size={18} /></button>
          <TimecodeField value={tc} rate={doc.frameRate} onJump={jump} className="tc-display" testId="program-tc" />
          <span className="tc-duration">/ {formatDuration(total)}</span>
          {rate !== 0 && rate !== 1 && <span className="pill mono" data-testid="shuttle-rate">{rate > 0 ? '▶' : '◀'} {Math.abs(rate)}×</span>}
          <span className="spacer" />
        </div>
      </div>
      <div className="viewer-controls" data-testid="program-buttons">
        <div className="volume">
          <button className="btn ghost icon sm" aria-label={muted ? 'Unmute the preview' : 'Mute the preview'} title="Preview volume (the meter reads the sequence before it)" onClick={() => setMuted(!muted)} data-testid="program-mute">
            {muted || monitorVolume === 0 ? <VolumeX size={15} /> : monitorVolume < 0.5 ? <Volume1 size={15} /> : <Volume2 size={15} />}
          </button>
          <input type="range" min={0} max={1} step={0.05} value={muted ? 0 : monitorVolume} aria-label="Preview volume" onChange={(e) => { setMonitorVolume(Number(e.target.value)); setMuted(false); }} />
        </div>
        {overlayMenu}
        {/* 1.7.1: one switch for every format (the single view, Guides and each Side by side panel). */}
        <button type="button" className={`btn ghost icon sm ${safeArea ? 'on' : ''}`} aria-pressed={safeArea} aria-label="Safe areas"
          title="Safe areas in every format: action 93 %, title 90 %" onClick={() => setSafeArea(!safeArea)} data-testid="safe-area-toggle"><SquareDashed size={15} /></button>
        <span style={{ flex: 1 }} />
        {onAddMarker && <button className="btn ghost sm" onClick={() => onAddMarker(clock.get())} title="Add a marker at the playhead (M)" data-testid="add-marker"><BookmarkPlus size={14} /> Marker</button>}
        <button className="btn ghost icon sm" onClick={toggleFullscreen} aria-label="Fullscreen" title="Fullscreen (double-click the picture)">{fullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}</button>
      </div>
    </div>
  );
}
